import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { ROLE_META } from '../utils/constants';
import { EditUserModal } from '../components/modals/EditUserModal';
import { Icon } from '../components/common/Icon';
import { approveUser, revokeUserApproval } from '../services/userService';

/**
 * Settings Page - Company, Users, Pipeline, Data Management
 */
export default function SettingsPage() {
  const ctx = useContext(AppContext);
  const navigate = useNavigate();
  const { 
    settings, 
    setSettings, 
    users, 
    restaurants, 
    showToast, 
    setModal,
    loadUsers,
    currentUser 
  } = ctx;
  
  const [form, setForm] = useState({...settings});
  const [tab, setTab] = useState('company');
  const [selectedRole, setSelectedRole] = useState('marketer');
  const [editingUser, setEditingUser] = useState(null);

  // Calculate workload stats for each user
  const getUserStats = (user) => {
    if (!restaurants || !user) return null;
    const today = new Date().toISOString().split('T')[0];
    
    if (user.role === 'marketer') {
      const userRestaurants = restaurants.filter(r => r.assignedTo === user.id);
      const drafts = userRestaurants.filter(r => r.status === 'data_review').length;
      const submitted = userRestaurants.filter(r => r.status === 'ready_for_rep').length;
      return { type: 'marketer', drafts, submitted, total: userRestaurants.length };
    } else if (user.role === 'rep') {
      const userRestaurants = restaurants.filter(r => r.assignedRep === user.id);
      const visitsToday = userRestaurants.filter(r => r.visitDate === today).length;
      const closedDeals = userRestaurants.filter(r => r.status === 'deal_closed').length;
      return { type: 'rep', visitsToday, closedDeals, total: userRestaurants.length };
    }
    return null;
  };

  const save = () => { 
    setSettings(form); 
    showToast('✅ تم الحفظ'); 
  };

  const exportCSV = () => {
    const hdr = ['name','ownerName','phone','area','status','mapsUrl','notes'];
    const csv = [
      hdr.join(','),
      ...(restaurants || []).map(r => 
        hdr.map(k => `"${(r[k] || '').replace(/"/g, '""')}"`).join(',')
      )
    ].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'gosnel_contacts.csv';
    a.click();
    showToast('✅ تم التصدير');
  };

  const toggleUser = id => {
    // This would need a service call in production
    showToast('تم التحديث');
  };

  const toggleUserApproval = async (user) => {
    try {
      if (user.is_approved_to_work) {
        // Revoke approval
        await revokeUserApproval(user.id);
        showToast(`⚠️ تم إلغاء اعتماد ${user.name} - لن يتمكن من رؤية جهات الاتصال`);
      } else {
        // Approve user
        await approveUser(user.id, currentUser.id);
        showToast(`✅ تم اعتماد ${user.name} - يمكنه الآن رؤية جهات الاتصال`);
      }
      loadUsers();
    } catch (err) {
      showToast('❌ فشل في تحديث حالة الاعتماد', 'error');
    }
  };

  const deleteUser = async (user) => {
    if (!confirm(`⚠️ هل أنت متأكد من حذف "${user.name}" نهائياً؟\n\nهذا الإجراء لا يمكن التراجع عنه وسيتم حذف:\n- جميع بيانات المستخدم\n- جميع المطاعم المعيّنة له\n- جميع سجلات النشاط`)) {
      return;
    }

    try {
      const { supabase } = await import('../services/supabaseClient');
      
      // First, unassign all restaurants assigned to this user
      if (user.role === 'marketer') {
        await supabase
          .from('restaurants')
          .update({ assignedTo: null })
          .eq('assignedTo', user.id);
      } else if (user.role === 'rep') {
        await supabase
          .from('restaurants')
          .update({ assignedRep: null, claimed_by: null, status: 'new' })
          .eq('assignedRep', user.id);
      }

      // Delete the user
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', user.id);

      if (error) throw error;

      showToast(`✅ تم حذف ${user.name} نهائياً`);
      loadUsers();
    } catch (err) {
      console.error('Delete user error:', err);
      showToast('❌ فشل في حذف المستخدم', 'error');
    }
  };

  const copyUserCredentials = async (user) => {
    const rm = ROLE_META[user.role] || { label: user.role, icon: '👤' };
    
    const credentialsText = `🎉 مرحباً بك في نظام GoSnel CRM!

━━━━━━━━━━━━━━━━━━━━━━━
📋 بيانات تسجيل الدخول
━━━━━━━━━━━━━━━━━━━━━━━

👤 الاسم: ${user.name}
🔑 اسم المستخدم: ${user.email || user.username}
🔒 كلمة المرور: ${user.password || user.password_hash || 'تواصل مع المدير'}
🎭 الدور: ${rm.label}

━━━━━━━━━━━━━━━━━━━━━━━
🌐 رابط تسجيل الدخول
━━━━━━━━━━━━━━━━━━━━━━━

🔗 https://crm.gosnel.com

━━━━━━━━━━━━━━━━━━━━━━━
📝 خطوات تسجيل الدخول
━━━━━━━━━━━━━━━━━━━━━━━

1️⃣ افتح الرابط: https://crm.gosnel.com
2️⃣ أدخل اسم المستخدم: ${user.email || user.username}
3️⃣ أدخل كلمة المرور
4️⃣ اضغط على زر "تسجيل الدخول"

━━━━━━━━━━━━━━━━━━━━━━━
📚 المستندات المشتركة (مطلوب القراءة)
━━━━━━━━━━━━━━━━━━━━━━━

⚠️ مهم جداً: بعد تسجيل الدخول، يجب عليك قراءة جميع المستندات في قسم "المستندات المشتركة" قبل البدء بالعمل

📖 هذه المستندات تحتوي على:
• إجراءات العمل الأساسية
• السياسات والتعليمات المهمة
• معلومات ضرورية لأداء مهامك بشكل صحيح

✅ تأكيد القراءة مطلوب من جميع الموظفين

━━━━━━━━━━━━━━━━━━━━━━━
⚠️ ملاحظات مهمة
━━━━━━━━━━━━━━━━━━━━━━━

🔐 لا تشارك بيانات الدخول مع أي شخص
💾 احفظ هذه الرسالة في مكان آمن
📱 النظام يعمل على الجوال والكمبيوتر

━━━━━━━━━━━━━━━━━━━━━━━
💻 الدعم الفني
━━━━━━━━━━━━━━━━━━━━━━━

لأي مساعدة، تواصل مع مدير النظام

GoSnel CRM - نظام إدارة العلاقات مع العملاء`;

    try {
      await navigator.clipboard.writeText(credentialsText);
      showToast(`✅ تم نسخ بيانات ${user.name} بنجاح`);
    } catch (err) {
      console.error('Failed to copy:', err);
      showToast('❌ فشل في النسخ', 'error');
    }
  };

  const formatLastLogin = (lastLogin, lastActive, isOnline) => {
    if (!lastLogin) return '—';
    const now = new Date();
    const loginTime = new Date(lastLogin);
    const activeTime = lastActive ? new Date(lastActive) : loginTime;
    const diffMinutes = Math.floor((now - activeTime) / (1000 * 60));
    
    if (isOnline && diffMinutes < 5) return '🟢 متصل الآن';
    if (diffMinutes < 1) return 'نشط الآن';
    if (diffMinutes < 60) return `منذ ${diffMinutes} دقيقة`;
    if (diffMinutes < 1440) return `منذ ${Math.floor(diffMinutes / 60)} ساعة`;
    return loginTime.toLocaleDateString('ar-AE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const S = {
    input: { width: '100%', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', color: '#000000', fontSize: 13, fontFamily: "'Cairo',sans-serif", outline: 'none', boxSizing: 'border-box', display: 'block' },
    label: { color: '#4d6680', fontSize: 11, display: 'block', marginBottom: 6, fontWeight: 700 },
    th: { padding: '11px 14px', color: '#3d5470', fontSize: 11, fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap' },
    td: { padding: '11px 14px', verticalAlign: 'middle' },
    btnPrimary: { background: 'linear-gradient(135deg,#4ecdc4,#38b2ac)', border: 'none', borderRadius: 10, padding: '10px 18px', color: '#fff', cursor: 'pointer', fontFamily: "'Cairo',sans-serif", fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 },
    btnSec: { background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 10, padding: '9px 18px', color: '#4d6680', cursor: 'pointer', fontFamily: "'Cairo',sans-serif", fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 },
  };

  return (
    <>
      {/* Mobile Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .settings-container {
            max-width: 100% !important;
            padding: 0 12px !important;
          }
          
          .settings-tabs {
            flex-wrap: wrap !important;
          }
          
          .settings-tabs button {
            flex: 1 1 calc(50% - 4px) !important;
            font-size: 12px !important;
            padding: 10px 8px !important;
          }
          
          .users-table-wrapper {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
          }
          
          .users-table {
            min-width: 900px !important;
          }
          
          .users-table th,
          .users-table td {
            padding: 8px 10px !important;
            font-size: 12px !important;
          }
          
          .company-form-grid {
            grid-template-columns: 1fr !important;
          }
          
          .action-buttons {
            flex-direction: column !important;
            gap: 4px !important;
          }
          
          .action-buttons button {
            width: 100% !important;
            justify-content: center !important;
          }
        }
      `}</style>

      <div className="settings-container" style={{ animation: 'sIn 0.3s ease', maxWidth: 840 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: '#000000', fontSize: 20, fontWeight: 900, marginBottom: 4 }}>⚙️ الإعدادات</h1>
        <p style={{ color: '#3d5470', fontSize: 13 }}>إدارة الشركة والمستخدمين والبيانات</p>
      </div>

      {/* Tabs */}
      <div className="settings-tabs" style={{ display: 'flex', gap: 4, marginBottom: 22, background: '#ffffff', borderRadius: 12, padding: 5, border: '1px solid #e2e8f0' }}>
        {[['company', '🏢 الشركة'], ['users', '👥 المستخدمون'], ['pipeline', '🔄 الأدوار'], ['data', '📊 البيانات']].map(([k, l]) => (
          <button 
            key={k} 
            onClick={() => setTab(k)} 
            style={{ 
              flex: 1, 
              padding: '8px 0', 
              background: tab === k ? '#e2e8f0' : 'transparent',
              border: 'none', 
              borderRadius: 8, 
              color: tab === k ? '#f1f5f9' : '#3d5470', 
              cursor: 'pointer',
              fontFamily: "'Cairo',sans-serif", 
              fontSize: 13, 
              fontWeight: tab === k ? 700 : 400 
            }}
          >
            {l}
          </button>
        ))}
      </div>

      {/* Company Tab */}
      {tab === 'company' && (
        <div style={{ background: '#ffffff', borderRadius: 16, padding: 26, border: '1px solid #e2e8f0' }}>
          <div className="company-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
            {[['اسم الشركة', 'companyName'], ['شعار الشركة (URL)', 'companyLogo'], ['كود الدولة (مثال: 971)', 'countryCode']].map(([l, k]) => (
              <div key={k}>
                <label style={S.label}>{l}</label>
                <input value={form[k]} onChange={e => setForm(p => ({ ...p, [k]: e.target.value }))} style={S.input} />
              </div>
            ))}
            <div>
              <label style={S.label}>اللون الرئيسي</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={form.primaryColor} onChange={e => setForm(p => ({ ...p, primaryColor: e.target.value }))}
                  style={{ width: 46, height: 40, border: '1px solid #e2e8f0', borderRadius: 9, background: 'transparent', cursor: 'pointer' }} />
                <input value={form.primaryColor} onChange={e => setForm(p => ({ ...p, primaryColor: e.target.value }))} style={{ ...S.input, flex: 1 }} />
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={S.label}>رسالة واتساب الافتراضية</label>
            <textarea value={form.whatsappMsg} onChange={e => setForm(p => ({ ...p, whatsappMsg: e.target.value }))} style={{ ...S.input, height: 70, resize: 'vertical' }} />
          </div>
          {form.companyLogo && (
            <div style={{ marginBottom: 18, display: 'flex', gap: 12, alignItems: 'center', background: '#ffffff', borderRadius: 12, padding: 14, border: '1px solid #e2e8f0' }}>
              <img src={form.companyLogo} alt="preview" style={{ height: 40, maxWidth: 140, objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
              <span style={{ color: '#4d6680', fontSize: 12 }}>معاينة الشعار</span>
            </div>
          )}
          <button onClick={save} style={{ ...S.btnPrimary, background: `linear-gradient(135deg,${form.primaryColor},#38b2ac)` }}>💾 حفظ الإعدادات</button>
        </div>
      )}

      {/* Users Tab */}
      {tab === 'users' && (
        <div style={{ background: '#ffffff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <span style={{ color: '#000000', fontWeight: 700 }}>المستخدمون ({users.length})</span>
            <button onClick={() => setModal({ type: 'add_user' })} style={S.btnPrimary}>+ مستخدم</button>
          </div>
          <div className="users-table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="users-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#ffffff' }}>
                {['الاسم', 'الدخول', 'الدور', 'الإنتاجية', 'آخر دخول', 'الاعتماد', 'الحالة', 'إجراء'].map(h => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const rm = ROLE_META[u.role] || { label: u.role, color: '#64748b', icon: '👤' };
                const lastLoginText = formatLastLogin(u.last_login, u.last_active, u.is_online);
                const stats = getUserStats(u);
                
                return (
                  <tr key={u.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={S.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: '#000000', fontWeight: 700 }}>{u.name}</span>
                        {u.role !== 'admin' && !u.is_approved_to_work && (
                          <span style={{
                            padding: '2px 8px',
                            background: '#f59e0b22',
                            border: '1px solid #f59e0b44',
                            borderRadius: 6,
                            color: '#f59e0b',
                            fontSize: 10,
                            fontWeight: 700,
                          }}>
                            تحت التدريب
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={S.td}><code style={{ color: '#4d6680', fontSize: 12 }}>{u.email || u.username}</code></td>
                    <td style={S.td}>
                      <span style={{ background: rm.color + '22', color: rm.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                        {rm.label}
                      </span>
                    </td>
                    <td style={S.td}>
                      {stats ? (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          {stats.type === 'marketer' && (
                            <span style={{ padding: '4px 8px', borderRadius: 6, background: '#4ecdc422', color: '#4ecdc4', fontSize: 11, fontWeight: 700 }}>
                              📝 {stats.drafts}/{stats.submitted}
                            </span>
                          )}
                          {stats.type === 'rep' && (
                            <>
                              <span style={{ padding: '4px 8px', borderRadius: 6, background: '#10b98122', color: '#10b981', fontSize: 11, fontWeight: 700 }}>
                                ✅ {stats.closedDeals}
                              </span>
                              <span style={{ padding: '4px 8px', borderRadius: 6, background: '#fb923c22', color: '#fb923c', fontSize: 11, fontWeight: 700 }}>
                                📅 {stats.visitsToday}
                              </span>
                            </>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#64748b', fontSize: 11 }}>—</span>
                      )}
                    </td>
                    <td style={{ ...S.td, fontSize: 12, color: '#000000' }}>
                      <span>{lastLoginText}</span>
                    </td>
                    <td style={S.td}>
                      {/* Admin users always approved, no toggle needed */}
                      {u.role === 'admin' ? (
                        <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: '#10b98122', color: '#10b981', border: '1px solid #10b98144' }}>
                          ✅ دائم
                        </span>
                      ) : (
                        <button
                          onClick={() => toggleUserApproval(u)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: 'pointer',
                            fontFamily: "'Cairo', sans-serif",
                            border: u.is_approved_to_work ? '1px solid #10b98144' : '1px solid #f59e0b44',
                            background: u.is_approved_to_work ? '#10b98122' : '#f59e0b22',
                            color: u.is_approved_to_work ? '#10b981' : '#f59e0b',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = u.is_approved_to_work ? '0 0 8px #10b98144' : '0 0 8px #f59e0b44';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          {u.is_approved_to_work ? '✅ معتمد' : '⏳ بانتظار'}
                        </button>
                      )}
                    </td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span 
                          title={u.is_online ? 'متصل' : 'غير متصل'}
                          style={{
                            width: 10, height: 10, borderRadius: '50%',
                            background: u.is_online ? '#10b981' : '#64748b',
                            boxShadow: u.is_online ? '0 0 8px #10b98188' : 'none',
                            display: 'inline-block'
                          }}
                        />
                        <span style={{ color: u.active ? '#34d399' : '#f87171', fontSize: 12 }}>{u.active ? 'نشط' : 'موقوف'}</span>
                      </div>
                    </td>
                    <td style={S.td}>
                      <div className="action-buttons" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button 
                          onClick={() => setEditingUser(u)}
                          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #4ecdc4', background: 'transparent', color: '#4ecdc4', cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}
                          title="تعديل المستخدم"
                        >
                          ✏️ تعديل
                        </button>
                        {(u.role === 'marketer' || u.role === 'rep') && (
                          <button 
                            onClick={() => {
                              navigate(`/${currentUser.role}/contacts`);
                              localStorage.setItem('gosnel_filter_user', JSON.stringify({
                                userId: u.id,
                                userName: u.name,
                                role: u.role
                              }));
                            }}
                            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #a78bfa', background: 'transparent', color: '#a78bfa', cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}
                            title="عرض العمل"
                          >
                            👁️ عرض العمل
                          </button>
                        )}
                        <button 
                          onClick={() => copyUserCredentials(u)}
                          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #10b981', background: 'transparent', color: '#10b981', cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}
                          title="نسخ بيانات الدخول"
                        >
                          📋 نسخ
                        </button>
                        {u.role !== 'admin' && (
                          <SmBtn color={u.active ? '#ef4444' : '#10b981'} onClick={() => toggleUser(u.id)}>
                            {u.active ? 'إيقاف' : 'تفعيل'}
                          </SmBtn>
                        )}
                        {u.role !== 'admin' && (
                          <button 
                            onClick={() => deleteUser(u)}
                            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #dc2626', background: '#dc262622', color: '#ef4444', cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}
                            title="حذف نهائي"
                          >
                            🗑️ حذف
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Pipeline Tab */}
      {tab === 'pipeline' && (
        <div style={{ background: '#ffffff', borderRadius: 16, padding: 24, border: '1px solid #e2e8f0' }}>
          <p style={{ color: '#4d6680', fontSize: 13, marginBottom: 20 }}>خلاصة صلاحيات كل دور في مسار العمل</p>
          <div style={{ display: 'grid', gap: 12 }}>
            {[
              { role: 'marketer', stages: ['new→data_review'], uploads: ['شعار', 'قائمة 1', 'قائمة 2'] },
              { role: 'data_entry', stages: ['data_review→ready_for_rep'], uploads: ['تحميل القوائم فقط'] },
              { role: 'sales_manager', stages: ['ready_for_rep→invoice_pending', 'pending_closure→deal_closed', 'أي مرحلة←إرجاع'], uploads: ['تعديل أي حقل'] },
              { role: 'accountant', stages: ['invoice_pending→in_field'], uploads: ['فاتورة'] },
              { role: 'rep', stages: ['in_field→pending_closure'], uploads: ['رخصة تجارية', 'هوية المالك'] },
            ].map(item => {
              const rm = ROLE_META[item.role];
              return (
                <div key={item.role} style={{ background: '#ffffff', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: rm.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{rm.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: rm.color, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{rm.label}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {item.stages.map(s => <span key={s} style={{ background: '#e2e8f0', color: '#3b82f6', padding: '2px 10px', borderRadius: 20, fontSize: 11 }}>{s}</span>)}
                      {item.uploads.map(u => <span key={u} style={{ background: '#e2e8f0', color: '#000000', padding: '2px 10px', borderRadius: 20, fontSize: 11 }}>📤 {u}</span>)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Data Tab */}
      {tab === 'data' && (
        <div style={{ display: 'grid', gap: 14 }}>
          {[
            { icon: '📥', title: 'استيراد CSV', desc: 'رفع جهات اتصال جديدة من ملف CSV مع ربط الأعمدة تلقائياً', action: () => setModal({ type: 'import_csv' }), label: 'استيراد', color: '#4ecdc4' },
            { icon: '📤', title: 'تصدير CSV', desc: 'تحميل جميع جهات الاتصال الحالية كملف CSV', action: exportCSV, label: 'تصدير', color: '#34d399' },
          ].map((item, i) => (
            <div key={i} style={{ background: '#ffffff', borderRadius: 14, padding: 20, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 18 }}>
              <span style={{ fontSize: 34 }}>{item.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#000000', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{item.title}</div>
                <div style={{ color: '#4d6680', fontSize: 13 }}>{item.desc}</div>
              </div>
              <button onClick={item.action} style={{ ...S.btnPrimary, background: `linear-gradient(135deg,${item.color},${item.color}88)` }}>{item.label}</button>
            </div>
          ))}
          <div style={{ background: '#ffffff', borderRadius: 14, padding: 20, border: '1px solid #e2e8f0' }}>
            <div style={{ color: '#000000', fontWeight: 700, fontSize: 15, marginBottom: 14 }}>📊 ملخص البيانات</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
              {[
                ['الكلي', (restaurants || []).length, '#4d6680'],
                ['قيد العمل', (restaurants || []).filter(r => !['deal_closed'].includes(r.status)).length, '#4ecdc4'],
                ['مغلق', (restaurants || []).filter(r => r.status === 'deal_closed').length, '#34d399'],
                ['مرجَع', (restaurants || []).filter(r => r.returnNote).length, '#f97316']
              ].map(([l, v, c]) => (
                <div key={l} style={{ background: '#ffffff', borderRadius: 10, padding: '12px 14px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#3d5470', fontSize: 10, marginBottom: 4 }}>{l}</div>
                  <div style={{ color: c, fontSize: 24, fontWeight: 900 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      <EditUserModal 
        isOpen={!!editingUser}
        onClose={() => setEditingUser(null)}
        showToast={showToast}
        user={editingUser}
        onUserUpdated={() => {
          if (loadUsers) loadUsers();
        }}
      />
    </div>
    </>
  );
}

// Helper: Small Button
function SmBtn({ color, onClick, children }) {
  return (
    <button onClick={onClick} style={{ background: color + '18', border: `1px solid ${color}44`, borderRadius: 7, color, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontFamily: "'Cairo',sans-serif", fontWeight: 700, whiteSpace: 'nowrap', transition: 'all 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.background = color + '33'}
      onMouseLeave={e => e.currentTarget.style.background = color + '18'}>
      {children}
    </button>
  );
}
